import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/services/auth';
import { postUserFlags } from './api';

import type { UserFlags } from './api';

type setUserFlagsProps = {
  accountId: number | undefined;
  userId: number | undefined;
  flags: Partial<UserFlags>;
};

export const useSetUserFlags = () => {
  return useMutation({
    mutationFn: async (props: setUserFlagsProps) => postUserFlags(props.accountId, props.userId, props.flags),
  });
};

/**
 * Auto-refresh toggle state for list pages, seeded from and persisted to the
 * `save_auto_refresh` user flag (Ember `<ScheduledTask>` parity). The caller
 * drives the actual polling from the returned `autoRefresh` (e.g. a query's
 * `refetchInterval`) and renders the toggle via `<AutoRefreshControl>`.
 */
export function useAutoRefresh(): { autoRefresh: boolean; onAutoRefreshChange: (next: boolean) => void } {
  const auth = useAuth();
  const { mutate: persistUserFlags } = useSetUserFlags();
  const [autoRefresh, setAutoRefresh] = useState(() => auth.user?.user_flags?.save_auto_refresh === true);

  const onAutoRefreshChange = useCallback(
    (next: boolean) => {
      setAutoRefresh(next);
      const accountId = auth.account?.id;
      const userId = auth.user?.id;
      if (accountId != null && userId != null) {
        persistUserFlags({ accountId, userId, flags: { save_auto_refresh: next } });
      }
    },
    [auth.account?.id, auth.user?.id, persistUserFlags],
  );

  return { autoRefresh, onAutoRefreshChange };
}
